import * as ExcelJS from 'exceljs';
import { format } from 'date-fns';
import {
  STATUS_CONFIG,
  getBorrowerName,
  getLoanOfficerName,
  type PipelineLoan
} from '../types';
import { buildPipelineLineModel } from '../components/pipelineLineModel';
import { flattenActivityEvents } from '../components/activityReportModel';
import { toPacificDateKey } from '../reporting';
import {
  PERFORMANCE_METRIC_KEYS,
  type CohortBasis,
  type PerformanceLoanDetail,
  type PerformanceMetricKey,
  type PipelinePerformanceExportInput
} from './types';

const METRIC_LABELS: Record<PerformanceMetricKey, string> = {
  created: 'Created',
  application: 'Applications',
  locked: 'Locked',
  funded: 'Funded'
};

const HEADER_FILL: ExcelJS.Fill = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FF1F2937' }
};

function styleHeader(row: ExcelJS.Row): void {
  row.eachCell((cell) => {
    cell.fill = HEADER_FILL;
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    cell.alignment = { vertical: 'middle' };
  });
}

function fitColumns(sheet: ExcelJS.Worksheet, minimum = 12, maximum = 34): void {
  sheet.columns.forEach((column) => {
    let width = minimum;
    column.eachCell?.({ includeEmpty: true }, (cell) => {
      width = Math.max(width, String(cell.value ?? '').length + 2);
    });
    column.width = Math.min(width, maximum);
  });
}

function filterLabel(input: PipelinePerformanceExportInput): string {
  return [
    `Loan officer: ${input.filters.loanOfficer}`,
    `Investor: ${input.filters.investor}`,
    `Channel: ${input.filters.channel}`,
    `Product: ${input.filters.product}`
  ].join(' | ');
}

function addDetailSheet(
  workbook: ExcelJS.Workbook,
  name: string,
  loans: PerformanceLoanDetail[],
  input: PipelinePerformanceExportInput
): void {
  const sheet = workbook.addWorksheet(name);
  const title = sheet.addRow([name]);
  title.getCell(1).font = { bold: true, size: 14 };
  sheet.addRow(['Selected range', input.model.currentRange.label]);
  sheet.addRow(['Filters', filterLabel(input)]);
  sheet.addRow([]);
  const header = sheet.addRow([
    'Loan #',
    'Borrower',
    'Loan Officer',
    'Event Date',
    'Amount',
    'Folder / Status',
    'Channel',
    'Created Date',
    'Application Date',
    'Lock Date',
    'Funded Date',
    'Investor',
    'Product'
  ]);
  styleHeader(header);
  loans.forEach((loan) => {
    const row = sheet.addRow([
      loan.loanNumber,
      loan.borrower,
      loan.loanOfficer,
      loan.eventDate,
      loan.amount,
      loan.folder,
      loan.channel,
      loan.createdDate,
      loan.applicationDate,
      loan.lockDate,
      loan.fundedDate,
      loan.investor || '',
      loan.product || ''
    ]);
    row.getCell(5).numFmt = '$#,##0';
  });
  sheet.views = [{ state: 'frozen', ySplit: header.number }];
  sheet.autoFilter = { from: `A${header.number}`, to: `M${header.number}` };
  fitColumns(sheet);
}

function eventDateKey(value: string | null | undefined): string | null {
  return toPacificDateKey(value);
}

function addMilestoneSheets(
  workbook: ExcelJS.Workbook,
  loans: PipelineLoan[],
  input: PipelinePerformanceExportInput
): void {
  const flow = workbook.addWorksheet('Milestone Flow');
  const activity = workbook.addWorksheet('Team Activity');
  const flowHeader = flow.addRow(['Milestone', 'Event Date', 'Loan #', 'Borrower', 'Loan Officer', 'Amount', 'Channel']);
  const activityHeader = activity.addRow(['Employee', 'Role', 'Milestone', 'Event Date', 'Loan #', 'Borrower', 'Loan Officer', 'Amount']);
  styleHeader(flowHeader);
  styleHeader(activityHeader);

  const range = {
    from: new Date(`${input.model.currentRange.from}T12:00:00`),
    to: new Date(`${input.model.currentRange.to}T12:00:00`)
  };
  const lineModel = buildPipelineLineModel(loans, range);
  lineModel.rows.forEach((period) => {
    (lineModel.detailsByKey.get(period.key) || []).forEach((detail) => {
      const flowRow = flow.addRow([
        detail.label,
        eventDateKey(detail.doneDate),
        detail.loan.trans_details_loan_number || '',
        getBorrowerName(detail.loan),
        getLoanOfficerName(detail.loan),
        detail.loan.trans_details_loan_amt || 0,
        detail.loan.channel || ''
      ]);
      flowRow.getCell(6).numFmt = '$#,##0';
    });
  });

  flattenActivityEvents(loans, range).forEach((event) => {
    const activityRow = activity.addRow([
      event.doneBy || 'Unassigned',
      event.role,
      STATUS_CONFIG[event.status]?.label || event.status,
      event.ptDay,
      event.loan.trans_details_loan_number || '',
      getBorrowerName(event.loan),
      getLoanOfficerName(event.loan),
      event.loan.trans_details_loan_amt || 0
    ]);
    activityRow.getCell(8).numFmt = '$#,##0';
  });

  [flow, activity].forEach((sheet) => {
    sheet.views = [{ state: 'frozen', ySplit: 1 }];
    fitColumns(sheet);
  });
}

function addCohortsSheet(workbook: ExcelJS.Workbook, input: PipelinePerformanceExportInput): void {
  const sheet = workbook.addWorksheet('Cohorts');
  const header = sheet.addRow([
    'Basis',
    'Period',
    'Cohort Units',
    'Cohort Volume',
    'Application Units',
    'Application %',
    'Locked Units',
    'Locked %',
    'Funded Units',
    'Funded %',
    'Excluded Anomalies'
  ]);
  styleHeader(header);
  (['application', 'created'] as CohortBasis[]).forEach((basis) => {
    input.model.cohorts[basis].rows.forEach((row) => {
      const dataRow = sheet.addRow([
        basis === 'application' ? 'Applications' : 'Created',
        row.label,
        row.cohort.units,
        row.cohort.volume,
        row.application.units,
        row.application.percentage / 100,
        row.locked.units,
        row.locked.percentage / 100,
        row.funded.units,
        row.funded.percentage / 100,
        row.anomalyCount
      ]);
      dataRow.getCell(4).numFmt = '$#,##0';
      [6, 8, 10].forEach((column) => {
        dataRow.getCell(column).numFmt = '0.0%';
      });
    });
  });
  sheet.addRow([]);
  const folderHeader = sheet.addRow([
    'Basis',
    'Current Folder',
    'Units',
    'Volume',
    'Share'
  ]);
  styleHeader(folderHeader);
  (['application', 'created'] as CohortBasis[]).forEach((basis) => {
    input.model.cohorts[basis].folderDistribution.forEach((folder) => {
      const row = sheet.addRow([
        basis === 'application' ? 'Applications' : 'Created',
        folder.folder,
        folder.units,
        folder.volume,
        folder.percentage / 100
      ]);
      row.getCell(4).numFmt = '$#,##0';
      row.getCell(5).numFmt = '0.0%';
    });
  });
  sheet.views = [{ state: 'frozen', ySplit: 1 }];
  fitColumns(sheet);
}

export async function exportPipelinePerformanceWorkbook(input: PipelinePerformanceExportInput): Promise<void> {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'LendWise';
  workbook.created = new Date();

  const summary = workbook.addWorksheet('Summary');
  summary.addRow(['Pipeline Performance']);
  summary.getCell('A1').font = { bold: true, size: 16 };
  summary.addRow(['Selected range', `${input.model.currentRange.label} (${input.model.currentRange.from} to ${input.model.currentRange.to})`]);
  summary.addRow(['Business days', input.model.currentRange.businessDays]);
  summary.addRow(['Includes today', input.model.currentRange.includesToday ? 'Yes' : 'No']);
  summary.addRow([
    'Comparison range',
    input.model.comparisonRange.isEmpty
      ? input.model.comparisonRange.label
      : `${input.model.comparisonRange.label} (${input.model.comparisonRange.from} to ${input.model.comparisonRange.to})`
  ]);
  summary.addRow(['Refreshed', input.refreshedAt ? format(input.refreshedAt, 'MM/dd/yyyy h:mm a') : '']);
  summary.addRow(['Loan officer filter', input.filters.loanOfficer]);
  summary.addRow(['Investor filter', input.filters.investor]);
  summary.addRow(['Channel filter', input.filters.channel]);
  summary.addRow(['Product filter', input.filters.product]);
  summary.addRow([]);
  summary.addRow(['Methodology', 'Population excludes Test, (Trash), Transferred Data, and soft-deleted loans. Created = Encompass 2025; Applications = Encompass 3142; Locked = buy-side, then sell-side, then request date; Funded = matched payout fund date for Brokered loans when present, otherwise Encompass funded date. Closing date is never used. Amount = Encompass 1109. Dates are inclusive Pacific calendar dates and include today.']);
  summary.addRow([]);
  const summaryHeader = summary.addRow([
    'Metric',
    'Current Loans',
    'Current Volume',
    'Current Loans / Business Day',
    'Current Volume / Business Day',
    'Comparison Loans',
    'Comparison Volume',
    'Comparison Loans / Business Day',
    'Comparison Volume / Business Day',
    'Units Pace Δ',
    'Volume Pace Δ'
  ]);
  styleHeader(summaryHeader);
  PERFORMANCE_METRIC_KEYS.forEach((metric) => {
    const value = input.model.summaries[metric];
    const row = summary.addRow([
      METRIC_LABELS[metric],
      value.current.units,
      value.current.volume,
      value.current.unitsPerDay,
      value.current.volumePerDay,
      input.model.comparisonRange.isEmpty ? '' : value.comparison.units,
      input.model.comparisonRange.isEmpty ? '' : value.comparison.volume,
      input.model.comparisonRange.isEmpty ? '' : value.comparison.unitsPerDay,
      input.model.comparisonRange.isEmpty ? '' : value.comparison.volumePerDay,
      value.unitsPerDayDeltaPct === null ? '' : value.unitsPerDayDeltaPct / 100,
      value.volumePerDayDeltaPct === null ? '' : value.volumePerDayDeltaPct / 100
    ]);
    row.getCell(3).numFmt = '$#,##0';
    row.getCell(4).numFmt = '0.00';
    row.getCell(5).numFmt = '$#,##0';
    row.getCell(7).numFmt = '$#,##0';
    row.getCell(8).numFmt = '0.00';
    row.getCell(9).numFmt = '$#,##0';
    row.getCell(10).numFmt = '0.0%';
    row.getCell(11).numFmt = '0.0%';
  });
  fitColumns(summary, 14, 60);

  const periods = workbook.addWorksheet('Period Breakdown');
  const periodHeader = periods.addRow([
    'Current Period',
    'Comparison Period',
    'Business Days',
    ...PERFORMANCE_METRIC_KEYS.flatMap((metric) => [
      `${METRIC_LABELS[metric]} Current Units`,
      `${METRIC_LABELS[metric]} Current Volume`,
      `${METRIC_LABELS[metric]} Comparison Units`,
      `${METRIC_LABELS[metric]} Comparison Volume`
    ])
  ]);
  styleHeader(periodHeader);
  const periodByKey = new Map(input.model.periods.map((period) => [period.key, period]));
  input.model.trend.forEach((point) => {
    const period = periodByKey.get(point.key);
    const row = periods.addRow([
      point.label,
      point.comparisonLabel,
      period?.businessDays ?? '',
      ...PERFORMANCE_METRIC_KEYS.flatMap((metric) => [
        point.hasCurrent === false ? '' : point.metrics[metric].current.units,
        point.hasCurrent === false ? '' : point.metrics[metric].current.volume,
        point.hasComparison === false ? '' : point.metrics[metric].comparison.units,
        point.hasComparison === false ? '' : point.metrics[metric].comparison.volume
      ])
    ]);
    PERFORMANCE_METRIC_KEYS.forEach((_metric, index) => {
      row.getCell(5 + index * 4).numFmt = '$#,##0';
      row.getCell(7 + index * 4).numFmt = '$#,##0';
    });
  });
  periods.views = [{ state: 'frozen', ySplit: 1 }];
  fitColumns(periods);

  addCohortsSheet(workbook, input);
  addMilestoneSheets(workbook, input.loans, input);
  PERFORMANCE_METRIC_KEYS.forEach((metric) => {
    addDetailSheet(
      workbook,
      `${METRIC_LABELS[metric]} Details`,
      input.model.summaries[metric].loans,
      input
    );
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `PipelinePerformance_${new Date().toISOString().slice(0, 10)}.xlsx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
